import {runInAction} from 'mobx';
import {logger} from '../logger';
import {ProductState} from './i';
import {isFiniteNonNegativeNumber, PricingUi} from './pricing-request/pricing-request';

/**
 * Server-rendered opening price (product.cshtml emits `var __preload_price = {price, price_discount, fp}` on a
 * default, priced opening, `null` otherwise). The page seeds the price UI from it before the first
 * /api/pricing answer, but only when the fingerprint `fp` of the state the server priced equals the state the
 * first pricing request carries; the live answer then always wins, and any disagreement is reported through
 * the logger (one Sentry issue, `ssr-opening-price-mismatch`).
 */

/**
 * The ProductState fields that decide the price: everything the server's setProductConfig and ProductGetPrice
 * read, plus `wholesale` (the server prices with the session's side; the key pins the client to the same one).
 * DF4's ProductOpening.Fingerprint emits these 22 keys in this order; keep the two lists identical.
 */
export const PRICE_STATE_KEYS = [
    'is_caf',
    'ProductTypeID',
    'docWidth',
    'docHeight',
    'docWidth2',
    'docHeight2',
    'moulding_id',
    'matcombo_id',
    'mat1_id',
    'mat2_id',
    'insignia_id',
    'glass_id',
    'ep_id',
    'lino_id',
    'lino_is_dyf',
    'lino_dyf_color',
    'is_horizontal',
    'category_selected_no_new_product',
    'image_upload_hash3',
    'image_upload_hash4',
    'override_d1_img',
    'wholesale',
] as const;

export type PriceStateKey = (typeof PRICE_STATE_KEYS)[number];

/** The price-relevant part of the state the server priced, as it arrives in `__preload_price.fp` (untrusted shape). */
export type OpeningPriceFingerprint = Partial<Record<PriceStateKey, unknown>>;

/** The client side of the comparison: the `ps` of the first /api/pricing payload. Partial so callers and tests can pass only the price keys. */
export type OpeningPriceState = Partial<Pick<ProductState, PriceStateKey>>;

export type OpeningPricePreload = {
    /** The list price (`result_decimal` of the server's opening pricing call). */
    price: number;
    /** The price after the store discount (`result_decimal_discount`); equal to `price` when there is none. */
    price_discount: number;
    fp: OpeningPriceFingerprint;
};

export type OpeningSeedIds = {
    product_id: number;
    store_id: number;
};

export type OpeningPriceMismatch = {
    /** `fingerprint`: the server priced a different state, the seed was not applied. `price`: same state, different answer. */
    kind: 'fingerprint' | 'price';
    product_id: number | null;
    store_id: number | null;
    /** The fingerprint keys whose client and server values differ; empty for a `price` mismatch. */
    keys: string[];
    seed: {price: number; price_discount: number};
    /** The first live /api/pricing answer; null for a `fingerprint` mismatch (no answer has arrived yet). */
    live: {price: number; price_discount: number} | null;
};

export const OPENING_PRICE_MISMATCH = 'ssr-opening-price-mismatch';

declare var __preload_price: OpeningPricePreload | null | undefined;

const typeDefault = (reference: unknown): string => {
    if (typeof reference === 'number') {
        return '0';
    }
    if (typeof reference === 'boolean') {
        return 'false';
    }
    return '';
};

/**
 * Comparable text for one fingerprint value. `reference` is the other side's value: a null or undefined value
 * takes the reference's type default ('0' for a number, 'false' for a boolean, '' otherwise), so an unset client
 * field agrees with the server's 0 / false / null. Numbers print through Number() so 14.000 and 14 agree,
 * booleans as 'true' / 'false', strings as they are.
 */
export const normalize = (value: unknown, reference: unknown): string => {
    if (value === null || value === undefined) {
        return typeDefault(reference);
    }
    if (typeof value === 'number') {
        return String(Number(value));
    }
    if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
    }
    return String(value);
};

/** The PRICE_STATE_KEYS whose client (`ps`) and server (`fp`) values differ after normalize(); empty when the states agree. */
export const openingFingerprintDiff = (ps: OpeningPriceState, fp: OpeningPriceFingerprint): string[] => {
    const keys: string[] = [];
    for (const key of PRICE_STATE_KEYS) {
        const client: unknown = ps[key];
        const server: unknown = fp[key];
        if (normalize(client, server) !== normalize(server, client)) {
            keys.push(key);
        }
    }
    return keys;
};

/** The page's `__preload_price`, or null when the global is missing, null, or not a well-formed preload. */
export const readPreloadPrice = (): OpeningPricePreload | null => {
    if (typeof __preload_price === 'undefined' || __preload_price === null) {
        return null;
    }
    const raw: unknown = __preload_price;
    if (typeof raw !== 'object' || Array.isArray(raw)) {
        return null;
    }
    const preload = raw as Partial<OpeningPricePreload>;
    if (!isFiniteNonNegativeNumber(preload.price) || !isFiniteNonNegativeNumber(preload.price_discount)) {
        return null;
    }
    if (!preload.fp || typeof preload.fp !== 'object' || Array.isArray(preload.fp)) {
        return null;
    }
    return {price: preload.price, price_discount: preload.price_discount, fp: preload.fp};
};

export const reportOpeningPriceMismatch = (details: OpeningPriceMismatch): void => {
    logger.error(OPENING_PRICE_MISMATCH, details);
};

/**
 * Applies the server's opening price to the price UI when `preload.fp` matches `ps` (the state the first
 * /api/pricing request carries): sets price, price_discount, price_resolved and price_seeded and returns true.
 * Returns false, leaving the UI alone, when there is no preload, when a price is already resolved, or when the
 * fingerprints differ - the last case is reported with the differing keys (`kind: 'fingerprint'`).
 */
export const seedOpeningPrice = (ui: PricingUi, ps: OpeningPriceState, preload: OpeningPricePreload | null, ids?: OpeningSeedIds): boolean => {
    if (!preload || ui.price_resolved) {
        return false;
    }
    const keys = openingFingerprintDiff(ps, preload.fp);
    if (keys.length > 0) {
        reportOpeningPriceMismatch({
            kind: 'fingerprint',
            product_id: ids?.product_id ?? null,
            store_id: ids?.store_id ?? null,
            keys,
            seed: {price: preload.price, price_discount: preload.price_discount},
            live: null,
        });
        return false;
    }
    runInAction(() => {
        ui.price = preload.price;
        ui.price_discount = preload.price_discount;
        ui.price_resolved = true;
        ui.price_seeded = true;
    });
    return true;
};
